import { useEffect, useState } from "react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Key, UserCircle, ChatTeardropText } from "@phosphor-icons/react";
import RefundSection from "@/components/RefundSection";
import LifetimeBadge from "@/components/LifetimeBadge";

export default function Settings() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const up = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // SMS form state — initialized from user, refreshed when user changes.
  const [phone, setPhone] = useState(user?.phone_number || "");
  const [smsOptIn, setSmsOptIn] = useState(!!user?.sms_opt_in);
  const [smsBusy, setSmsBusy] = useState(false);
  const [smsConfigured, setSmsConfigured] = useState(true);

  useEffect(() => {
    setPhone(user?.phone_number || "");
    setSmsOptIn(!!user?.sms_opt_in);
  }, [user]);

  useEffect(() => {
    api.get("/auth/sms-status")
      .then((r) => setSmsConfigured(!!r.data?.configured))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.next.length < 6) {
      setErr("New password must be at least 6 characters.");
      return;
    }
    if (form.next !== form.confirm) {
      setErr("New password and confirmation do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      toast.success("Password updated");
      setForm({ current: "", next: "", confirm: "" });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const savePhone = async () => {
    setSmsBusy(true);
    try {
      await api.patch("/auth/phone", { phone_number: phone });
      toast.success(phone ? "Phone updated" : "Phone cleared");
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSmsBusy(false);
    }
  };

  const toggleOptIn = async (next) => {
    setSmsOptIn(next);  // optimistic
    setSmsBusy(true);
    try {
      await api.patch("/auth/phone", { sms_opt_in: next });
      toast.success(next ? "SMS alerts ON" : "SMS alerts OFF");
      await refresh();
    } catch (e) {
      setSmsOptIn(!next);  // rollback
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSmsBusy(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="gf-overline">Settings</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">
        Account.
      </h1>

      <div className="mt-8 border border-zinc-200 bg-white p-6" data-testid="account-card">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-blue-700 flex items-center justify-center shrink-0">
            <UserCircle size={22} color="white" weight="regular" />
          </div>
          <div>
            <div className="gf-overline">Signed in as</div>
            <div className="font-display font-bold text-xl mt-1 flex items-center gap-2">
              {user?.name}
              <LifetimeBadge member={user} size="sm" />
            </div>
            <div className="text-sm text-zinc-600 font-mono">{user?.email}</div>
            {user?.organization && (
              <div className="text-xs text-zinc-500 mt-1">{user.organization}</div>
            )}
            <div className="gf-overline mt-3">Role: {user?.role}</div>
          </div>
        </div>
      </div>

      <div className="mt-6 border border-zinc-200 bg-white p-6" data-testid="sms-card">
        <div className="flex items-center gap-3">
          <ChatTeardropText size={18} />
          <div className="gf-overline">SMS alerts</div>
        </div>
        <p className="text-sm text-zinc-600 mt-2">
          {smsConfigured
            ? "Add a phone number, opt in, then enable SMS on individual saved searches to get text alerts when matching grants post."
            : "SMS is not configured server-side yet — your phone number can be saved but no SMS will be sent until the admin adds Twilio credentials."}
        </p>

        <div className="mt-6 grid sm:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <Label className="gf-overline">Phone number (E.164)</Label>
            <Input
              data-testid="sms-phone-input"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 415 555 1234"
              className="gf-sharp mt-2 border-zinc-300 bg-white font-mono"
            />
            <p className="text-[11px] font-mono text-zinc-500 mt-2">
              Bare 10-digit US numbers are auto-prefixed with +1. International OK.
            </p>
          </div>
          <Button
            onClick={savePhone}
            disabled={smsBusy}
            data-testid="sms-save-btn"
            variant="outline"
            className="gf-sharp border-zinc-300 uppercase font-bold tracking-wide"
          >
            {smsBusy ? "Saving…" : "Save phone"}
          </Button>
        </div>

        <div className="mt-6 flex items-center justify-between border-t border-zinc-200 pt-4">
          <div>
            <div className="font-semibold text-sm">Receive SMS alerts</div>
            <div className="text-xs text-zinc-500">
              Master toggle. Each saved search also has its own SMS toggle.
            </div>
          </div>
          <Switch
            data-testid="sms-opt-in-switch"
            checked={smsOptIn}
            onCheckedChange={toggleOptIn}
            disabled={smsBusy || !user?.phone_number}
          />
        </div>
        {!user?.phone_number && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 mt-3 font-mono">
            Save a phone number first to enable SMS alerts.
          </div>
        )}
      </div>

      <form
        onSubmit={submit}
        className="mt-6 border border-zinc-200 bg-white p-6"
        data-testid="change-password-form"
      >
        <div className="flex items-center gap-3">
          <Key size={18} />
          <div className="gf-overline">Change password</div>
        </div>
        <p className="text-sm text-zinc-600 mt-2">
          Choose a strong password. You'll remain signed in on this device after changing it.
        </p>

        <div className="mt-6 space-y-4">
          <div>
            <Label className="gf-overline">Current Password</Label>
            <Input
              data-testid="current-password"
              type="password"
              required
              value={form.current}
              onChange={up("current")}
              className="gf-sharp mt-2 border-zinc-300 bg-white"
              autoComplete="current-password"
            />
          </div>
          <div>
            <Label className="gf-overline">New Password</Label>
            <Input
              data-testid="new-password"
              type="password"
              required
              value={form.next}
              onChange={up("next")}
              className="gf-sharp mt-2 border-zinc-300 bg-white"
              autoComplete="new-password"
              placeholder="min 6 chars"
            />
          </div>
          <div>
            <Label className="gf-overline">Confirm New Password</Label>
            <Input
              data-testid="confirm-password"
              type="password"
              required
              value={form.confirm}
              onChange={up("confirm")}
              className="gf-sharp mt-2 border-zinc-300 bg-white"
              autoComplete="new-password"
            />
          </div>
          {err && (
            <div
              data-testid="change-password-error"
              className="text-sm text-red-700 bg-red-50 border border-red-200 p-3"
            >
              {err}
            </div>
          )}
          <Button
            type="submit"
            disabled={busy}
            data-testid="change-password-submit"
            className="gf-sharp bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wide"
          >
            {busy ? "Updating…" : "Update password"}
          </Button>
        </div>
      </form>
      <RefundSection />
    </div>
  );
}
